import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { diffWords } from "diff";
import { useTranslation } from "react-i18next";
import {
  FileCheck, FileWarning, AlertTriangle, X, Shield, DollarSign,
  Globe, Users, Trophy, ExternalLink, Copy, Check, UserPlus, Scale,
  GitCompare, History,
} from "lucide-react";

/* ---------------- Agreement diff viewer ---------------- */
function AgreementDiff({ current, previous, mode }) {
  const { t } = useTranslation();
  // Word-level diff: GitHub-style red/green with strikethrough on removals.
  const parts = useMemo(() => {
    if (!previous) return null;
    return diffWords(previous.body_md || "", current.body_md || "");
  }, [current, previous]);

  if (!previous) {
    return (
      <div data-testid="agreement-diff-nothing-prior"
           className="p-3 border border-white/10 rounded bg-white/[0.02] text-xs text-white/50">
        {t("governance.firstTime")}
      </div>
    );
  }

  if (mode === "side") {
    // Side-by-side: previous (red tint) vs current (green tint)
    return (
      <div data-testid="agreement-diff-side" className="grid md:grid-cols-2 gap-3">
        <div className="border border-rose-400/20 rounded bg-rose-500/[0.04] p-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-rose-300 font-mono mb-2">
            Previous · v{previous.version}
          </div>
          <div className="max-h-[45vh] overflow-y-auto text-xs text-white/80 whitespace-pre-wrap leading-relaxed">
            {previous.body_md}
          </div>
        </div>
        <div className="border border-emerald-400/20 rounded bg-emerald-500/[0.04] p-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-mono mb-2">
            New · v{current.version}
          </div>
          <div className="max-h-[45vh] overflow-y-auto text-xs text-white/80 whitespace-pre-wrap leading-relaxed">
            {current.body_md}
          </div>
        </div>
      </div>
    );
  }

  // Inline diff
  return (
    <div data-testid="agreement-diff-inline"
         className="max-h-[50vh] overflow-y-auto p-4 bg-black/30 border border-white/10 rounded text-sm text-white/80 whitespace-pre-wrap leading-relaxed font-mono">
      {parts.map((p, i) => {
        if (p.added) {
          return (
            <ins key={i} className="bg-emerald-500/25 text-emerald-100 no-underline rounded-sm px-0.5">
              {p.value}
            </ins>
          );
        }
        if (p.removed) {
          return (
            <del key={i} className="bg-rose-500/20 text-rose-200 line-through decoration-rose-300/60 rounded-sm px-0.5">
              {p.value}
            </del>
          );
        }
        return <span key={i} className="text-white/60">{p.value}</span>;
      })}
    </div>
  );
}

/* ---------------- Governance banner + modal (used in Command Center) ---------------- */
export function GovernanceBanner() {
  const { t } = useTranslation();
  const [outstanding, setOutstanding] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(null);
  const [diff, setDiff] = useState(null);     // {current, previous, has_prior}
  const [view, setView] = useState("body");    // 'body' | 'diff-inline' | 'diff-side'

  const load = () => api.get("/partners/me/agreements").then((r) => {
    setItems(r.data.items);
    setOutstanding(r.data.outstanding_signatures);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const sign = async (id) => {
    try {
      await api.post("/partners/me/agreements/sign", { agreement_id: id, accepted: true });
      toast.success("Signed.");
      setReading(null);
      setDiff(null);
      setView("body");
      load();
    } catch { toast.error("Couldn't sign."); }
  };

  const openAgreement = async (a) => {
    try {
      const r = await api.get(`/partners/me/agreements/${a.id}`);
      setReading(r.data);
      setView("body");
      // Fetch diff metadata lazily — if there's a prior version
      // we'll show a "Review changes" switcher.
      try {
        const d = await api.get(`/partners/me/agreements/${a.id}/diff`);
        setDiff(d.data);
      } catch { setDiff(null); }
    } catch { toast.error("Couldn't open."); }
  };

  if (outstanding === 0) return null;

  return (
    <>
      <div data-testid="governance-banner"
           className="mt-6 border border-amber-400/40 bg-amber-500/[0.06] rounded-lg p-4 flex items-start gap-3">
        <FileWarning size={18} className="text-amber-300 shrink-0 mt-0.5"/>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            {t("governance.pending", { count: outstanding })}
          </div>
          <div className="text-xs text-white/60 mt-1">
            {t("governance.pendingHelp")}
          </div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="governance-review-btn"
                className="rounded-full bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold">
          {t("governance.review")}
        </Button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-[#141110] border border-white/15 rounded-lg max-w-2xl w-full p-6 my-10 space-y-3" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="text-white font-semibold">{t("governance.pendingTitle")}</div>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={16}/></button>
            </div>
            {items.filter(a => a.needs_signature).map((a) => (
              <div key={a.id} data-testid={`agreement-row-${a.id}`} className="border border-white/10 rounded p-3 bg-black/20">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-white font-medium text-sm">{a.title}</div>
                    <div className="text-[11px] text-white/40 font-mono mt-0.5">{a.kind} · v{a.version}</div>
                  </div>
                  <Button onClick={() => openAgreement(a)} data-testid={`agreement-open-${a.id}`}
                          className="rounded-full border border-white/15 bg-transparent text-white text-xs">{t("governance.readSign")}</Button>
                </div>
                {a.summary && <div className="text-xs text-white/60 mt-2 leading-relaxed">{a.summary}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {reading && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={() => { setReading(null); setDiff(null); }}>
          <div className="bg-[#141110] border border-white/15 rounded-lg max-w-4xl w-full p-6 my-10 space-y-4" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B878] font-mono">{reading.kind} · v{reading.version}</div>
                <div className="text-white font-semibold text-lg">{reading.title}</div>
              </div>
              <button onClick={() => { setReading(null); setDiff(null); }} className="text-white/40 hover:text-white"><X size={16}/></button>
            </div>

            {diff && diff.has_prior && (
              <div data-testid="agreement-diff-switcher" className="flex items-center gap-1 border border-white/10 rounded-full p-1 bg-white/[0.02] w-fit text-xs">
                <button onClick={() => setView("body")} data-testid="agreement-view-body"
                        className={`px-3 py-1 rounded-full transition ${view === "body" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
                  {t("governance.current")}
                </button>
                <button onClick={() => setView("diff-inline")} data-testid="agreement-view-diff-inline"
                        className={`px-3 py-1 rounded-full transition flex items-center gap-1 ${view === "diff-inline" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
                  <GitCompare size={11}/> {t("governance.inlineDiff")}
                </button>
                <button onClick={() => setView("diff-side")} data-testid="agreement-view-diff-side"
                        className={`px-3 py-1 rounded-full transition flex items-center gap-1 ${view === "diff-side" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
                  <History size={11}/> {t("governance.sideBySide")}
                </button>
              </div>
            )}
            {diff && diff.has_prior && diff.previous && view !== "body" && (
              <div className="text-[11px] text-white/40 font-mono">
                {t("governance.compare", { prev: diff.previous.version, curr: diff.current.version })}
              </div>
            )}

            {view === "body" && (
              <div data-testid="agreement-body" className="max-h-[50vh] overflow-y-auto p-4 bg-black/30 border border-white/10 rounded text-sm text-white/80 whitespace-pre-wrap leading-relaxed">
                {reading.body_md}
              </div>
            )}
            {view !== "body" && diff && (
              <AgreementDiff
                current={diff.current || reading}
                previous={diff.previous}
                mode={view === "diff-side" ? "side" : "inline"}
              />
            )}

            <Button onClick={() => sign(reading.id)} data-testid="agreement-sign-confirm"
                    className="w-full rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold">
              <Check size={13} className="mr-1.5"/> {t("governance.accept", { version: reading.version })}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- Network tab (Regional cohort) ---------------- */
export function NetworkTab() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const load = () => api.get("/partners/me/network").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-white/40">Loading…</div>;

  const shareUrl = `${window.location.origin}${data.share_url}`;
  const copy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Referral link copied.");
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div data-testid="partner-network" className="space-y-6">
      <div className="border border-[#D4B878]/25 rounded-lg p-5 bg-gradient-to-br from-[#D4B878]/[0.05] to-transparent">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B878] mb-1 font-mono flex items-center gap-1">
          <UserPlus size={11}/> Your referral link
        </div>
        <div className="flex items-center gap-2 mt-2">
          <input readOnly value={shareUrl} data-testid="referral-url"
                 className="flex-1 bg-black/30 border border-white/10 rounded px-3 py-2 text-xs text-[#E8D6A3] font-mono"/>
          <Button onClick={copy} data-testid="referral-copy"
                  className="rounded-full bg-[#D4B878] text-black text-xs font-semibold">
            {copied ? <Check size={13} className="mr-1"/> : <Copy size={13} className="mr-1"/>}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <div className="text-[11px] text-white/40 mt-2">
          Code: <span className="font-mono text-[#E8D6A3]">{data.referral_code}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          {k:"sub_partner_count",label:"Sub-partners"},
          {k:"pending_count",label:"Pending"},
          {k:"active_opps",label:"Active opps"},
          {k:"cohort_commission_total",label:"Cohort total",currency:true},
        ].map((s) => (
          <div key={s.k} data-testid={`network-stat-${s.k}`} className="border border-white/10 rounded p-3 bg-white/[0.02]">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">{s.label}</div>
            <div className="text-xl font-semibold text-white font-mono mt-1">
              {s.currency ? `INR ${(data.stats[s.k] || 0).toLocaleString()}` : (data.stats[s.k] || 0)}
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono mb-2">
          Active sub-partners ({data.sub_partners.length})
        </div>
        <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
          {data.sub_partners.length === 0 && (
            <div data-testid="network-subpartners-empty" className="p-6 text-center text-white/40 text-sm">
              No sub-partners yet. Share your referral link.
            </div>
          )}
          {data.sub_partners.map((p) => (
            <div key={p.id} data-testid={`network-row-${p.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{p.name}</div>
                <div className="text-[11px] text-white/40 font-mono truncate">
                  {p.email} · {p.city ? `${p.city}, ` : ""}{p.country} · {(p.categories || []).join(", ")}
                </div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#D4B878]">{p.tier}</span>
            </div>
          ))}
        </div>
      </div>

      {data.pending_applications.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono mb-2">
            Pending applications ({data.pending_applications.length})
          </div>
          <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
            {data.pending_applications.map((p) => (
              <div key={p.id} data-testid={`network-pending-${p.id}`} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{p.name}</div>
                  <div className="text-[11px] text-white/40 font-mono truncate">{p.email} · {p.country}</div>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#E8D6A3]">{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Payout currency selector ---------------- */
export function PayoutCurrencyCard({ currentCurrency, onUpdate }) {
  const [val, setVal] = useState(currentCurrency || "INR");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/partners/me/payout-currency", { payout_currency: val });
      toast.success(`Payout currency set to ${val}.`);
      onUpdate?.(val);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="payout-currency-card" className="border border-white/10 rounded p-3 bg-white/[0.02] flex items-center gap-2 flex-wrap">
      <DollarSign size={13} className="text-[#D4B878]"/>
      <div className="text-xs text-white/60 flex-1">Payout currency</div>
      <select value={val} onChange={(e)=>setVal(e.target.value)} data-testid="payout-currency-select"
              className="bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
        {["INR","USD","EUR","GBP","AED","SGD","AUD","CAD"].map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <Button onClick={save} disabled={busy || val === currentCurrency} data-testid="payout-currency-save"
              className="rounded-full bg-[#D4B878] text-black text-xs font-semibold">
        {busy ? "..." : "Save"}
      </Button>
    </div>
  );
}
